import { StatusCodes } from "http-status-codes";
import { CatalogItemServiceResponse, CatalogCategoryServiceResponse } from "interfaces";
import {
    CATALOGICAL_ENDPOINT,
    CATALOGICAL_HARDWARE_AUX_ENDPOINT,
    CATALOGICAL_HARDWARE_ENDPOINT,
} from "../configuration/config";
import { BaseService } from "./base-service";
import { getString } from "common/ui_string_labels/ui-strings-utils";

/**
 * Enum representing the possible sort directions for catalog item queries.
 */
export enum SortDirection {
    ASCENDING = "asc",
    DESCENDING = "desc",
}

/**
 * The default values for the CatalogicalService getAllCatalogItems function
 */
export const CATALOGICAL_DEFAULT = {
    /**
     * As of right now the limit is set to 500 as a decision made by @omarjzc and @kristgo
     *
     * It works as the upper limit for the fetch.
     */
    LIMIT: 500,
    OFFSET: 0,
    SORT_BY: "name",
    SORT_DIRECTION: SortDirection.ASCENDING,
    FILTERS: "",
    FUZZY: false,
    KEYWORDS: "",
};

/**
 * The sort field for taxonomy-related queries.
 * Used to sort items by their taxonomy identifier.
 */
export const TAXONOMY_SORT_BY = "taxonomyId";

/**
 * Error messages used in the CatalogicalService for validation.
 */
export const GET_ALL_CATALOG_ERRORS = {
    INVALID_LIMITS: "Invalid limit parameters for pagination.",
    INVALID_OFFSET: "Invalid offset parameters for pagination.",
    INVALID_FILTERS_JSON: "Invalid JSON format in filters parameter.",
};

/**
 * The path to retrieve the failed fetch string in the ui-strings
 */
export const FAILED_FETCH_STRING_PATH = "services.catalogical-service.failedFetch";

/**
 * Filter that only retrieves standard items from the itseham domain
 */
export const STANDARD_ITEM_FILTER = { type: "Standard" };

/**
 * Filter that only retrieved non standard items from the itseham domain
 */
export const NON_STANDARD_ITEM_FILTER = { type: "NonStandard" };

/**
 * Filter that only retrieves categories form the itsehamaux domain
 */
export const CATEGORY_FILTER = { type: "Category" };

export interface CatalogicalDefaultParams {
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortDirection?: SortDirection;
    filters?: string;
    fuzzy?: boolean;
    keywords?: string;
    signal?: AbortSignal;
}

/**
 * CatalogicalService class extends BaseService and provides methods for interacting with the catalogical service.
 */
export class CatalogicalService extends BaseService {
    // Instance of CatalogicalService
    public static instance = new CatalogicalService(CATALOGICAL_ENDPOINT);

    /**
     * Validates the provided limit parameter for pagination.
     * @param {number} limit - The limit parameter to validate.
     * @throws {Error} If the limit is less than 1.
     */
    private validateLimit(limit: number): void {
        if (limit < 1) {
            throw new Error(GET_ALL_CATALOG_ERRORS.INVALID_LIMITS);
        }
    }

    /**
     * Validates the provided offset parameter for pagination.
     * @param {number} offset - The limit parameter to validate.
     * @throws {Error} If the limit is less than 0.
     */
    private validateOffset(offset: number): void {
        if (offset < 0) {
            throw new Error(GET_ALL_CATALOG_ERRORS.INVALID_OFFSET);
        }
    }

    /**
     * Formats and validates query parameters for catalogical API requests.
     *
     * @param {CatalogicalDefaultParams} parameters - The parameters object containing query options
     * @return {URLSearchParams} - The formatted URL search parameters ready for API request
     */
    private formatQueryParameters({
        limit = CATALOGICAL_DEFAULT.LIMIT,
        offset = CATALOGICAL_DEFAULT.OFFSET,
        sortBy = CATALOGICAL_DEFAULT.SORT_BY,
        sortDirection = CATALOGICAL_DEFAULT.SORT_DIRECTION,
        filters = CATALOGICAL_DEFAULT.FILTERS,
        fuzzy = CATALOGICAL_DEFAULT.FUZZY,
        keywords = CATALOGICAL_DEFAULT.KEYWORDS,
    }: CatalogicalDefaultParams): URLSearchParams {
        this.validateLimit(limit);
        this.validateOffset(offset);

        const queryParams = new URLSearchParams({
            limit: `${limit}`,
            offset: `${offset}`,
            sortBy: `${sortBy}`,
            sortDirection: `${sortDirection}`,
        });
        if (filters && filters.length > 0) {
            queryParams.append("filter", filters);
        }
        if (fuzzy && keywords.length > 0) {
            queryParams.append("fuzzy", `${fuzzy}`);
            queryParams.append("keywords", keywords);
        }

        return queryParams;
    }

    /**
     * Fetches item from the itse catalog that are standard with optional sorting, filtering, and fuzzy search.
     *
     * @param {number} limit - The maximum number of items to retrieve. Default is 500.
     * @param {number} offset - The offset from which to start retrieving items. Default is 0.
     * @param {string} sortBy - The field by which to sort the items. Default is "name".
     * @param {sortDirection} sortDirection - The direction of sorting, either ascending or descending. Default is ascending.
     * @param {string} filters - A JSON string of filter conditions.
     * @param {boolean} fuzzy - Indicates whether a fuzzy search should be performed. Default is false.
     * @param {string} keywords - The keywords to use for a fuzzy search. Ignored if fuzzy is false. Default is an empty string ("").
     * @returns {Promise<CatalogItemServiceResponse>} A promise that resolves to the response with catalog items.
     */
    public async getAllCatalogItems(parameters: CatalogicalDefaultParams): Promise<CatalogItemServiceResponse> {
        let receivedFilters = {};
        try {
            receivedFilters = JSON.parse(parameters.filters || "{}");
        } catch (error) {
            throw new Error(GET_ALL_CATALOG_ERRORS.INVALID_FILTERS_JSON);
        }
        const updatedFilters = { ...receivedFilters, ...STANDARD_ITEM_FILTER };
        const updatedParameters = { ...parameters, filters: JSON.stringify(updatedFilters) };

        const queryParams = this.formatQueryParameters(updatedParameters);

        const url = `${CATALOGICAL_HARDWARE_ENDPOINT}?${queryParams.toString()}`;

        const response = await this.fetch(url, { signal: parameters.signal });

        if ([StatusCodes.OK, StatusCodes.CREATED].includes(response.status)) {
            return (await response.json()) as CatalogItemServiceResponse;
        }

        throw new Error(getString(FAILED_FETCH_STRING_PATH));
    }

    /**
     * Formats the catalog response to extract taxonomy IDs from non-standard orders.
     *
     * @param {CatalogItemServiceResponse} nonStandardOrder - The catalog response containing non-standard order items
     * @return {string[]} - Array of taxonomy IDs extracted from the catalog items
     */
    private formatNonStandardOrdersId(nonStandardOrder: CatalogItemServiceResponse): string[] {
        let nonStandardOrdersId: string[] = [];

        nonStandardOrder.data.forEach((item) => {
            nonStandardOrdersId.push(item.taxonomyId);
        });

        return nonStandardOrdersId;
    }

    /**
     * Retrieves taxonomy IDs for non-standard orders with pagination and sorting options.
     *
     * @param {number} limit - The maximum number of items to retrieve. Default is 500
     * @param {SortDirection} sortDirection - The direction of sorting, either ascending or descending. Default is ascending
     * @param {string} sortBy - The field by which to sort the items. Default is "name"
     * @return {Promise<string[]>} - A promise that resolves to an array of taxonomy IDs for non-standard orders
     */
    public async getNonStandardOrdersId(
        limit: number = CATALOGICAL_DEFAULT.LIMIT,
        sortDirection: SortDirection = CATALOGICAL_DEFAULT.SORT_DIRECTION,
        sortBy: string = TAXONOMY_SORT_BY,
    ): Promise<string[]> {
        const queryParams = new URLSearchParams({
            limit: `${limit}`,
            sortBy: `${sortBy}`,
            sortDirection: `${sortDirection}`,
        });

        queryParams.append("filter", JSON.stringify(NON_STANDARD_ITEM_FILTER));

        const url = `${CATALOGICAL_HARDWARE_ENDPOINT}?${queryParams.toString()}`;

        const response = await this.fetch(url);

        if ([StatusCodes.OK, StatusCodes.CREATED].includes(response.status)) {
            const data = await response.json();
            return this.formatNonStandardOrdersId(data);
        }

        throw new Error(getString(FAILED_FETCH_STRING_PATH));
    }

    /**
     * Retrieves all catalog categories with pagination and filtering options.
     *
     * @param {CatalogicalDefaultParams} parameters - The parameters for fetching categories including limit, offset, and filters
     * @return {Promise<CatalogCategoryServiceResponse>} - A promise that resolves to the catalog category service response
     */
    public async getAllCategoriesItems(parameters: CatalogicalDefaultParams): Promise<CatalogCategoryServiceResponse> {
        let receivedFilters = {};
        try {
            receivedFilters = JSON.parse(parameters.filters || "{}");
        } catch (error) {
            throw new Error(GET_ALL_CATALOG_ERRORS.INVALID_FILTERS_JSON);
        }
        const updatedFilters = { ...receivedFilters, ...CATEGORY_FILTER };
        const updatedParameters = { ...parameters, filters: JSON.stringify(updatedFilters) };

        const queryParams = this.formatQueryParameters(updatedParameters);

        const url = `${CATALOGICAL_HARDWARE_AUX_ENDPOINT}?${queryParams.toString()}`;
        const response = await this.fetch(url);

        if ([StatusCodes.OK, StatusCodes.CREATED].includes(response.status)) {
            return response.json() as Promise<CatalogCategoryServiceResponse>;
        }

        throw new Error(getString(FAILED_FETCH_STRING_PATH));
    }
}
